<script setup lang="ts">
import { FullColorObject } from '@/composables/color';
import { Ref, computed, ref } from 'vue';

const props = defineProps<{
    colors: FullColorObject;
}>();

const emit = defineEmits<{
    (event: 'change', value: Partial<FullColorObject>): void;
}>();

const containerRef: Ref<HTMLDivElement | null> = ref(null);

const gradientColor = computed(() => {
    const rgb = props.colors.rgb;
    const rgbStr = [rgb.r, rgb.g, rgb.b].join(',');
    return 'linear-gradient(to right, rgba(' + rgbStr + ', 0) 0%, rgba(' + rgbStr + ', 1) 100%)';
});

const handleChange = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();

    const container = containerRef.value;

    // for some edge cases, container may not exist
    if (!container) return;

    const containerWidth = container.clientWidth;

    const xOffset = container.getBoundingClientRect().left + window.scrollX;
    const pageX = (e as MouseEvent).pageX || ((e as TouchEvent).touches ? (e as TouchEvent).touches[0].pageX : 0);
    const left = pageX - xOffset;

    let alpha;
    if (left < 0) {
        alpha = 0;
    } else if (left > containerWidth) {
        alpha = 1;
    } else {
        alpha = Math.round((left * 100) / containerWidth) / 100;
    }

    if (props.colors.a !== alpha) {
        emit('change', {
            hsl: { ...props.colors.hsl },
            a: alpha
        });
    }
};

const handleMouseUp = () => {
    unbindEventListeners();
};

const handleMouseDown = () => {
    window.addEventListener('mousemove', handleChange);
    window.addEventListener('mouseup', handleMouseUp);
};

const unbindEventListeners = () => {
    window.removeEventListener('mousemove', handleChange);
    window.removeEventListener('mouseup', handleMouseUp);
};
</script>

<template>
    <div class="c-alpha">
        <div class="c-alpha-gradient" :style="{ background: gradientColor }" />
        <div
            ref="containerRef"
            class="c-alpha-container"
            role="slider"
            @click="handleChange"
            @mousedown="handleMouseDown"
            @touchmove="handleChange"
            @touchstart="handleChange"
        >
            <div class="c-alpha-pointer" :style="{ left: colors.a * 100 + '%' }">
                <div class="c-alpha-picker" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
%position {
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    border-radius: 50rem;
}
.c-alpha,
.c-alpha-checkboard-wrap,
.c-alpha-gradient {
    @extend %position;
}

.c-alpha {
    .c-alpha-container {
        cursor: pointer;
        position: relative;
        z-index: 2;
        height: 100%;
        margin-right: 10px;
        margin-left: 5px;
        .c-alpha-pointer {
            z-index: 2;
            position: absolute;
            .c-alpha-picker {
                cursor: grab;
                width: 1.2rem;
                height: 1.2rem;
                border-radius: 50%;
                transform: translate(-6px, -2px);
                background: #fff;
                border: 3px solid blue;
                box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
            }
        }
    }
}
</style>
